import LocalStorageAdapter from '@/utils/LocalStorageAdapter'
import { objectContainsTruthyEntries } from '@/utils/tools'
import QueryEncoder from './QueryEncoder'

/**
 * @description
 * Like the QueryEncoder this class acts as a wrapper around specific data, it will then automatically update the query
 * in the url and the local storage to reflect the changes that will have occurred to the data.
 * It will then automatically decode the current url or local storage and update the data to reflect the changes.
 * The main difference between the QueryEncoder and this, is that LocalStorage will act as a backup if no query is found
 */
export default class LocalStorageEncoder extends QueryEncoder {
  /**
   * @param {any} data            - The data to wrap
   * @param {string} storageKey   - Used in determining the locale storage key and should be unique
   *                                (also used for queryPoint but can be overwritten in args)
   * @param {function} decoder    - The decoder used to decode the local storage strings
   * @param {function} encoder    - Encodes the data into a string, if none is given it will try to do so automatically
   * @param {any} defaultVal      - When decoding fails or does not find it's query endpoint it sets the data to this
   * @param {function} filter     - Allows you to filter out the data before it gets encoded for the localStorage.
   *                                The output of this function is passed to the encoder function
   * @param forceLocalStorage     - If we always want to use localstorage in all situations instead of query
   * @param {{}}  args            - Any extra arguments get pushed to the parent class
   */
  constructor({
    data = null,
    storageKey = '',
    decoder = null,
    encoder = null,
    defaultVal = null,
    filter = (_) => _,
    forceLocalStorage = false,
    ...args
  } = {}) {
    super({ queryPoint: storageKey, data, decoder, encoder, defaultVal, filter, ...args })
    this._localStorageKey = `${window.location.hostname}${window.location.pathname}.${storageKey}`
    this._force = forceLocalStorage

    // If we are the constructor we can safely decode now
    // If we don't have any data we will naively try to decode the data from the current query/localStorage
    if (this.constructor === LocalStorageEncoder && this._data === null) {
      this.decode()
    }
  }

  /**
   * @param {string} value - value to be saved in local storage
   * @private
   */
  _setInLocalStorage(value) {
    LocalStorageAdapter.set(this._localStorageKey, value)
  }

  /**
   * Gets the encoded data from local storage
   * @returns {boolean|number|any|[]}
   * @private
   */
  _getFromLocalStorage() {
    return LocalStorageAdapter.get({
      key: this._localStorageKey,
    })
  }

  /**
   * Deletes the current key in local storage so it no loger exits
   * @private
   */
  _removeFromLocalStorage() {
    return LocalStorageAdapter.remove(this._localStorageKey)
  }

  /**
   * Encodes the current data and updates it in both the local storage and the query in the url
   */
  encode() {
    const encoded = this._encode()

    if (encoded) {
      // Store value for later
      // handling array here because of a bug that let an array format get to here on the insights filters
      this._setInLocalStorage(encoded instanceof Array ? encoded.join() : encoded)
    } else {
      // If entry has been cleared, remove it from storage
      this._removeFromLocalStorage()
    }
    super.encode()
  }

  /**
   * Will try to decode the current data from the query (takes priority) or the local storage if no queries are found
   * */
  decode() {
    const storageValue = this._getFromLocalStorage()
    // If we force the use of localStorage, we update the values here
    if (this._force && storageValue) {
      this.push(storageValue)
      this._data = this._decode(storageValue)
      return
    }

    super.decode() // Let the QueryEncoder do its job
    const queryVal = this.queryValue
    // Do not use local storage if user is attempting to visit a url with some pre-set values
    const queryContainsAnySpecificValues = objectContainsTruthyEntries(
      QueryEncoder.decodeQueryIntoObject(window.location.search),
    )
    if (queryVal) {
      // Found a value in the url, so we remember it for later
      this._setInLocalStorage(queryVal)
    } else if (!queryContainsAnySpecificValues && storageValue) {
      // Nothing was found in the url, but we did find a value in storage, so use it and update the url
      this.push(storageValue)
      this._data = this._decode(storageValue)
    }
  }
}
